import React, { CSSProperties, useState } from 'react';
import styled, { css, useTheme } from 'styled-components/macro';
import {
  ContentState,
  convertToRaw,
  EditorState,
  Modifier,
  convertFromHTML,
  RichUtils,
} from 'draft-js';
import { Editor, SyntheticKeyboardEvent } from 'react-draft-wysiwyg';
import htmlToDraft from 'html-to-draftjs';
import draftToHtml from 'draftjs-to-html';
import './customEditorStyles.css';
import { AddVariableButton, CharacterCounter, SourceHTMLToggleButton } from '.';
import { SnippetType } from 'lib/const';
import {
  CtaFilesModal,
  getItemTypes,
} from 'app/pages/design/callsToAction/CtaFilesModal';
import { prepareTextForSMS } from 'lib/utils/functions';
import { ModalChooseSnippet } from 'app/pages/snippets/components';
import sanitizeHtml from 'sanitize-html';
import { PROMPT_TYPE } from 'react-covideo-ai-assist';
import { MdFolder } from 'react-icons/md';
import { Button } from 'react-covideo-common';
import { MagicButton } from '../MagicButton';
import { useCDK } from 'lib/hooks/useCdk';
import { BUTTON_VARIANT } from 'react-covideo-common/dist/components/Button/types';
import { useTranslation } from 'react-i18next';

type EditorWrapperProps = {
  padding: string;
};

const EditorWrapper = styled.div<EditorWrapperProps>`
  padding: ${({ padding }) => padding};
  position: relative;
`;

type HTMLEditorProps = {
  visible: boolean;
};

const HTMLEditor = styled.textarea<HTMLEditorProps>`
  display: block;
  min-height: 160px;
  box-sizing: border-box;
  resize: vertical;
  width: 100%;
  background: #1a1f29;
  color: ${({ theme }) => theme.colors.white[100]};
  border: 1px solid ${({ theme }) => theme.colors.neutral[40]};
  border-radius: 5px;
  padding: 16px 24px;
  font-family: 'Courier New', monospace;
  font-style: normal;
  font-weight: 500;
  font-size: 15px;
  line-height: 24px;
  ${({ visible }) =>
    !visible &&
    css`
      display: none;
    `}
`;

const TemplatesButtonWrapper = styled.div<{ showBeneathEditor: boolean }>`
  ${({ showBeneathEditor }) =>
    showBeneathEditor &&
    css`
      position: absolute;
      bottom: 8px;
      right: 0;
    `}
`;

const ToolbarCustomButton = styled.div`
  margin-right: 8px;
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.danger[80]};
  margin-top: 5px;
`;

type Props = {
  onTextEditorChange: (text: string) => void;
  initialContent?: string;
  height?: string;
  customPadding?: string;
  placeholder?: string;
  editorWrapperClassname?: string;
  toolbar: {};
  hideAddVariableButton?: boolean;
  addVariableButtonType?: 'button' | 'submit' | 'reset';
  snippetButtonPosition?: 'hidden' | 'inline' | 'bottom';
  snippetButtonIcon?: JSX.Element;
  showFilesButton?: boolean;
  showSourceButton?: boolean;
  snippetType?: SnippetType;
  showCharacterCounter?: boolean;
  forceSoftNewLine?: boolean /** SUS-1003 changes **/;
  dataCy?: string;
  showMagicButton?: boolean;
  magicBtnLabel?: string;
  magicBtnMargin?: string;
  magicHoverPopup?: JSX.Element;
  defaultVin?: string;
  error?: string;
  setSubject?: (value: string) => void;
  extendStyles?: {
    wrapper?: CSSProperties;
    variableButton?: CSSProperties;
    variableButtonContainer?: CSSProperties;
    dropdownContent?: CSSProperties;
    snippetButtonVariant?: BUTTON_VARIANT;
  };
};

export const TextEditor = (props: Props) => {
  const {
    toolbar,
    onTextEditorChange,
    initialContent,
    height,
    customPadding,
    placeholder,
    addVariableButtonType = 'submit',
    hideAddVariableButton = false,
    snippetButtonPosition = 'hidden',
    showFilesButton = false,
    snippetType = SnippetType.NoType,
    showCharacterCounter = false,
    showSourceButton = false,
    editorWrapperClassname = 'automotive-wrapper',
    forceSoftNewLine = false /** SUS-1003 changes **/,
    dataCy,
    showMagicButton = false,
    magicBtnLabel = '',
    magicHoverPopup,
    magicBtnMargin,
    defaultVin = '',
    setSubject,
    extendStyles,
    snippetButtonIcon,
    error = '',
  } = props;

  const { t } = useTranslation();
  const { colors } = useTheme();
  const ItemTypes = getItemTypes(t);
  const { secondaryToText } = useCDK();
  const [showHtmlEditor, setShowHtmlEditor] = useState(false);
  const [countCharacters, setCountCharacters] = useState(
    prepareTextForSMS(initialContent || '').length
  );
  const [showModalChooseSnippet, setShowModalChooseSnippet] = useState(false);
  const [showModalChooseFile, setShowModalChooseFile] = useState(false);
  const [editorHtml, setEditorHtml] = useState(initialContent);
  const [isTouched, setIsTouched] = useState(false);

  const blocksFromHTML = htmlToDraft(initialContent || '');
  const initialData = ContentState.createFromBlockArray(
    blocksFromHTML.contentBlocks,
    blocksFromHTML.entityMap
  );

  const [editorState, setEditorState] = useState(
    EditorState.createWithContent(initialData)
  );

  const addFile = (text: string) => {
    let content = text || '';
    if (content && content.indexOf('<p>') !== 0) {
      content = `<a href='${sanitizeHtml(text)}' target='_blank'>${sanitizeHtml(text)}</a>&nbsp;`;
    }

    // Convert sanitized HTML to DraftJS content
    const { contentBlocks, entityMap } = htmlToDraft(content);

    // Create a new content state
    const newContentState = ContentState.createFromBlockArray(
      contentBlocks,
      entityMap
    );

    // Replace content in the editor
    const updatedContent = Modifier.replaceWithFragment(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      newContentState.getBlockMap()
    );

    // Push the new content to the editor state
    const newEditorState = EditorState.push(
      editorState,
      updatedContent,
      'insert-fragment'
    );

    // Update the editor state
    onEditorStateChange(newEditorState);
  };

  const cleanHTML = (dirtyHtml: string) => {
    const cleanHtml = sanitizeHtml(dirtyHtml, {
      allowedTags: [
        'div',
        'p',
        'b',
        'i',
        'em',
        'small',
        'strong',
        'a',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'img',
        'blockquote',
        'br',
        'hr',
        'ul',
        'ol',
        'li',
      ],
      allowedAttributes: {
        a: ['href', 'name', 'target'],
        img: ['src', 'srcset', 'alt', 'title', 'width', 'height'],
      },
    });
    return cleanHtml;
  };

  const onChangeHTMLEditor = (e: any) => {
    const editorHTML = e.target.value;
    let editor;
    const contentBlock = convertFromHTML(cleanHTML(editorHTML));
    if (contentBlock) {
      const contentState = ContentState.createFromBlockArray(
        contentBlock.contentBlocks
      );
      editor = EditorState.createWithContent(contentState);
    } else {
      editor = EditorState.createEmpty();
    }
    onEditorStateChange(editor, editorHTML);
  };

  const handleReplaceContent = (text: string, subject?: string) => {
    let content = text || '';
    if (content && content.indexOf('<p>') !== 0) {
      content = `<p>${text}</p>`;
    }
    const blocksFromHTML = htmlToDraft(content);
    const blockData = ContentState.createFromBlockArray(
      blocksFromHTML.contentBlocks,
      blocksFromHTML.entityMap
    );
    onEditorStateChange(
      EditorState.push(editorState, blockData, 'insert-characters')
    );
    if (subject && setSubject) {
      setSubject(subject);
    }
  };

  const onEditorStateChange = (
    editorState: EditorState,
    htmlEditorContent = ''
  ) => {
    setEditorState(editorState);
    const editorContent =
      htmlEditorContent ||
      draftToHtml(convertToRaw(editorState.getCurrentContent()));
    setEditorHtml(editorContent);
    setCountCharacters(prepareTextForSMS(editorContent || '').length);
    onTextEditorChange(editorContent);
    setIsTouched(true);
  };

  const getTextFromHTMLEditor = () =>
    prepareTextForSMS(
      draftToHtml(convertToRaw(editorState.getCurrentContent()))
    );

  let toolbarCustomButtons: JSX.Element[] = [];

  if (showCharacterCounter) {
    toolbarCustomButtons = [
      ...toolbarCustomButtons,
      <CharacterCounter count={countCharacters} />,
    ];
  }
  if (showFilesButton) {
    toolbarCustomButtons = [
      ...toolbarCustomButtons,
      <ToolbarCustomButton>
        <Button
          variant={secondaryToText}
          text={t('Files')}
          onClick={() => setShowModalChooseFile(true)}
          icon={<MdFolder style={{ marginRight: 4 }} size={18} />}
        />
      </ToolbarCustomButton>,
    ];
  }
  if (!hideAddVariableButton) {
    toolbarCustomButtons = [
      ...toolbarCustomButtons,
      <ToolbarCustomButton style={extendStyles?.variableButtonContainer}>
        <AddVariableButton
          dropdownButtonType={addVariableButtonType}
          editorState={editorState}
          onEditorStateChange={onEditorStateChange}
          extendStyles={{
            button: { height: '33px', ...extendStyles?.variableButton },
            dropdownContent: extendStyles?.dropdownContent,
          }}
        />
      </ToolbarCustomButton>,
    ];
  }
  if (snippetButtonPosition !== 'hidden') {
    toolbarCustomButtons = [
      ...toolbarCustomButtons,
      <TemplatesButtonWrapper
        showBeneathEditor={snippetButtonPosition === 'bottom'}
      >
        <Button
          variant={extendStyles?.snippetButtonVariant || secondaryToText}
          text={t('Templates')}
          onClick={() => setShowModalChooseSnippet(true)}
          icon={snippetButtonIcon}
        />
      </TemplatesButtonWrapper>,
    ];
  }
  if (showSourceButton) {
    toolbarCustomButtons = [
      ...toolbarCustomButtons,
      <SourceHTMLToggleButton
        showHtmlEditor={showHtmlEditor}
        setShowHtmlEditor={setShowHtmlEditor}
        editorState={editorState}
        onEditorStateChange={onEditorStateChange}
      />,
    ];
  }
  if (showMagicButton) {
    const defaultPromptType =
      snippetType === SnippetType.Sms ? PROMPT_TYPE.SMS : PROMPT_TYPE.EMAIL;
    const margin = showCharacterCounter ? '0 0 0 8px' : '0 0 0 auto';
    toolbarCustomButtons = [
      ...toolbarCustomButtons,
      <MagicButton
        defaultPromptType={defaultPromptType}
        defaultVin={defaultVin}
        userInput={getTextFromHTMLEditor()}
        margin={magicBtnMargin || margin}
        handleSubmitGeneratedContent={(content: string) =>
          handleReplaceContent(content)
        }
        text={magicBtnLabel}
        hoverPopup={magicHoverPopup}
      />,
    ];
  }

  /** SUS-1003 changes **/
  const handleReturn = (
    e: SyntheticKeyboardEvent,
    editorState2: EditorState
  ) => {
    if (forceSoftNewLine) {
      const newEditorState = RichUtils.insertSoftNewline(editorState2);
      if (newEditorState !== editorState2) {
        onEditorStateChange(newEditorState);
      }
      return true;
    }
    return false;
  };
  /******/

  return (
    <>
      <EditorWrapper
        padding={
          snippetButtonPosition === 'bottom' || showSourceButton
            ? '0 0 48px'
            : '0'
        }
        style={extendStyles?.wrapper || {}}
      >
        <Editor
          webDriverTestID={dataCy}
          editorStyle={{
            minHeight: height || 300,
            background: colors.white[100],
            border: `1px solid ${colors.neutral[40]}`,
            borderRadius: '5px',
            padding: customPadding || '16px 20px',
            marginTop: 0,
            cursor: 'text',
            display: showSourceButton && showHtmlEditor ? 'none' : 'block',
          }}
          placeholder={placeholder}
          editorState={editorState}
          wrapperClassName={`${editorWrapperClassname} ${
            showHtmlEditor ? 'disable-formatting-buttons' : ''
          }`}
          onEditorStateChange={onEditorStateChange}
          toolbar={toolbar}
          toolbarCustomButtons={toolbarCustomButtons}
          handlePastedText={() => false}
          handleReturn={handleReturn} /** SUS-1003 changes **/
        />

        <HTMLEditor
          visible={showSourceButton && showHtmlEditor}
          value={editorHtml}
          onChange={onChangeHTMLEditor}
        />

        {error && isTouched && <ErrorText>{error}</ErrorText>}
      </EditorWrapper>
      {showModalChooseSnippet && (
        <ModalChooseSnippet
          snippetType={snippetType}
          handleModalClose={() => setShowModalChooseSnippet(false)}
          handleSubmit={handleReplaceContent}
        />
      )}
      {showModalChooseFile && (
        <CtaFilesModal
          view={ItemTypes[0].value}
          selectedFile={''}
          handleModalClose={() => {
            setShowModalChooseFile(false);
          }}
          onSelect={(drop: any) => {
            addFile(drop.shortlink);
            setShowModalChooseFile(false);
          }}
        />
      )}
    </>
  );
};
